import React, { useEffect, useState } from "react";
import "./Modal.css";
import { backdropDismiss } from "../../../../utils/backdropDismiss";
import { DAYS_ORDER } from "../constants";
import { addDays, clampDateToMidnight, startOfWeekMonday } from "../dateUtils";

const DAY_LABELS = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

function toDateInputValue(date) {
  if (!date) return "";
  const d = clampDateToMidnight(date);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function parseDateInputValue(value) {
  if (!value) return null;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function formatWeekRange(weekStart) {
  if (!weekStart) return "";
  const start = clampDateToMidnight(weekStart);
  const end = addDays(start, 6);
  const sameYear = start.getFullYear() === end.getFullYear();
  const startStr = start.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  const endStr = end.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `${startStr} – ${endStr}`;
}

function WeekPicker({ weekStart, onChange, disabled }) {
  const step = (deltaWeeks) => {
    if (disabled || !weekStart) return;
    onChange(clampDateToMidnight(addDays(weekStart, deltaWeeks * 7)));
  };

  const handleInput = (event) => {
    const picked = parseDateInputValue(event.target.value);
    if (picked) onChange(startOfWeekMonday(picked));
  };

  return (
    <div className={`dupWeekPicker${disabled ? " isDisabled" : ""}`}>
      <button
        type="button"
        className="dupStepBtn"
        onClick={() => step(-1)}
        disabled={disabled}
        aria-label="Previous week"
      >
        ‹
      </button>
      <div className="dupWeekCenter">
        <div className="dupWeekRange">{formatWeekRange(weekStart)}</div>
        <input
          type="date"
          className="dupWeekDateInput"
          value={toDateInputValue(weekStart)}
          onChange={handleInput}
          disabled={disabled}
          aria-label="Jump to week"
        />
      </div>
      <button
        type="button"
        className="dupStepBtn"
        onClick={() => step(1)}
        disabled={disabled}
        aria-label="Next week"
      >
        ›
      </button>
    </div>
  );
}

export default function DuplicateMenuModal({
  isOpen,
  onClose,
  onConfirm,
  defaultMode = "week",
  defaultSourceWeekStart = null,
  defaultTargetWeekStart = null,
}) {
  const [sourceMode, setSourceMode] = useState(defaultMode);
  const [sourceWeekStart, setSourceWeekStart] = useState(null);
  const [targetWeekStart, setTargetWeekStart] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    if (!isOpen) return;
    const thisWeekStart = startOfWeekMonday(new Date());
    setSourceMode(defaultMode === "master" ? "master" : "week");
    setSourceWeekStart(
      defaultSourceWeekStart ? clampDateToMidnight(defaultSourceWeekStart) : thisWeekStart
    );
    setTargetWeekStart(
      defaultTargetWeekStart ? clampDateToMidnight(defaultTargetWeekStart) : addDays(thisWeekStart, 7)
    );
    setSelected(new Set());
  }, [isOpen, defaultMode, defaultSourceWeekStart, defaultTargetWeekStart]);

  if (!isOpen) return null;

  const toggleDay = (dayKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(dayKey)) next.delete(dayKey);
      else next.add(dayKey);
      return next;
    });
  };

  const selectAllDays = () => setSelected(new Set(DAYS_ORDER));
  const clearDays = () => setSelected(new Set());

  const activeDays = DAYS_ORDER.filter((dayKey) => selected.has(dayKey));
  const sameWeek =
    sourceMode === "week" &&
    sourceWeekStart &&
    targetWeekStart &&
    clampDateToMidnight(sourceWeekStart).getTime() === clampDateToMidnight(targetWeekStart).getTime();
  const canSubmit = activeDays.length > 0 && !sameWeek;

  const handleRun = () => {
    if (!canSubmit) return;
    onConfirm({
      selectedDayKeys: activeDays,
      mode: sourceMode,
      sourceWeekStart: sourceMode === "master" ? null : clampDateToMidnight(sourceWeekStart),
      targetWeekStart: clampDateToMidnight(targetWeekStart),
    });
  };

  return (
    <div className="modalOverlay" {...backdropDismiss(onClose)}>
      <div className="modalBox dupModalBox" onClick={(e) => e.stopPropagation()}>
        <div className="modalTitleRow">
          <div className="modalTitle">Duplicate Schedule</div>
          <button className="modalCloseBtn" onClick={onClose}>
            X
          </button>
        </div>

        {/* From */}
        <div className="dupSection">
          <div className="dupSectionLabel">From</div>
          <div className="dupSegment" role="tablist" aria-label="Source type">
            <button
              type="button"
              className={`dupSegmentBtn${sourceMode === "week" ? " isActive" : ""}`}
              onClick={() => setSourceMode("week")}
            >
              Existing Week
            </button>
            <button
              type="button"
              className={`dupSegmentBtn${sourceMode === "master" ? " isActive" : ""}`}
              onClick={() => setSourceMode("master")}
            >
              Master Schedule
            </button>
          </div>
          {sourceMode === "master" ? (
            <div className="dupMasterCard">
              Copies from the <strong>Master Schedule</strong> template.
            </div>
          ) : (
            <WeekPicker weekStart={sourceWeekStart} onChange={setSourceWeekStart} />
          )}
        </div>

        <div className="dupArrow" aria-hidden="true">
          ↓
        </div>

        {/* To */}
        <div className="dupSection">
          <div className="dupSectionLabel">To</div>
          <WeekPicker weekStart={targetWeekStart} onChange={setTargetWeekStart} />
          {sameWeek && (
            <div className="dupWarn">
              Source and destination weeks are the same. Pick a different destination.
            </div>
          )}
        </div>

        {/* Days */}
        <div className="dupSection">
          <div className="dupDaysHeader">
            <div className="dupSectionLabel">Days to duplicate</div>
            <div className="dupQuickRow">
              <button type="button" className="dupQuickBtn" onClick={selectAllDays}>
                Select all
              </button>
              <button type="button" className="dupQuickBtn" onClick={clearDays}>
                Clear
              </button>
            </div>
          </div>
          <div className="dupDayGrid">
            {DAYS_ORDER.map((dayKey) => (
              <button
                key={dayKey}
                type="button"
                className={`dupDayPill${selected.has(dayKey) ? " isActive" : ""}`}
                onClick={() => toggleDay(dayKey)}
                aria-pressed={selected.has(dayKey)}
              >
                {DAY_LABELS[dayKey]}
              </button>
            ))}
          </div>
        </div>

        <div className="modalActions">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className={`btn btnPrimary${canSubmit ? "" : " disabled"}`}
            onClick={handleRun}
            disabled={!canSubmit}
          >
            Duplicate Selected Days
          </button>
        </div>
      </div>
    </div>
  );
}
